
import { useState, useRef, useEffect } from 'react'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'

export interface NumberInputProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  prefix?: string
  suffix?: string
  /** Number of decimal places to show in display mode. Full precision shown in edit mode. */
  displayDecimals?: number
  className?: string
  disabled?: boolean
  title?: string
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  prefix,
  suffix,
  displayDecimals,
  className,
  title,
}: NumberInputProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState(value.toString())
  const inputRef = useRef<HTMLInputElement>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const valueRef = useRef(value)

  // Keep valueRef in sync with value prop
  useEffect(() => {
    valueRef.current = value
  }, [value])

  useEffect(() => {
    if (!isEditing) {
      setInputValue(value.toString())
    }
  }, [value, isEditing])

  const performIncrement = () => {
    const currentValue = valueRef.current
    const newValue = currentValue + step
    if (max === undefined || newValue <= max) {
      onChange(newValue)
    }
  }

  const performDecrement = () => {
    const currentValue = valueRef.current
    const newValue = currentValue - step
    if (min === undefined || newValue >= min) {
      onChange(newValue)
    }
  }

  const startIncrement = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Exit edit mode if active
    if (isEditing) {
      setIsEditing(false)
    }

    performIncrement()

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(performIncrement, 100)
    }, 300)
  }

  const startDecrement = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Exit edit mode if active
    if (isEditing) {
      setIsEditing(false)
    }

    performDecrement()

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(performDecrement, 100)
    }, 300)
  }

  const stopRepeat = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      stopRepeat()
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)
    // Only update local input value, onChange fires on blur/enter
  }

  const handleInputBlur = () => {
    const numValue = parseFloat(inputValue)
    if (!isNaN(numValue)) {
      // Round to nearest step
      let clampedValue = Math.round(numValue / step) * step

      if (min !== undefined) clampedValue = Math.max(min, clampedValue)
      if (max !== undefined) clampedValue = Math.min(max, clampedValue)
      onChange(clampedValue)
      setInputValue(clampedValue.toString())
    } else {
      setInputValue(value.toString())
    }
    setIsEditing(false)
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur()
    } else if (e.key === 'Escape') {
      setInputValue(value.toString())
      setIsEditing(false)
    }
  }

  const handleContainerClick = () => {
    if (!isEditing) {
      setIsEditing(true)
      setTimeout(() => {
        inputRef.current?.select()
      }, 0)
    }
  }

  const displayValue = () => {
    // Format the number for display
    const formattedNumber = displayDecimals !== undefined
      ? value.toFixed(displayDecimals)
      : value.toString()

    if (prefix || suffix) {
      return `${prefix || ''}${formattedNumber}${suffix || ''}`
    }
    return formattedNumber
  }

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-1 h-8 w-[180px] border border-border rounded-md bg-background px-1',
        'hover:border-foreground/20 transition-colors',
        className
      )}
      title={title}
    >
      <button
        type="button"
        aria-label={title ? `Decrease ${title}` : 'Decrease value'}
        className="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded hover:bg-accent disabled:opacity-50 disabled:pointer-events-none transition-colors"
        onMouseDown={startDecrement}
        onMouseUp={stopRepeat}
        onMouseLeave={stopRepeat}
        onTouchStart={startDecrement}
        onTouchEnd={stopRepeat}
        disabled={min !== undefined && value <= min}
      >
        <Icon name="minus" className="h-3 w-3" />
      </button>

      <div
        className="flex min-w-0 flex-1 items-center justify-center px-1 cursor-text"
        onClick={handleContainerClick}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type="number"
            aria-label={title ? `${title} value` : 'Number value'}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            min={min}
            max={max}
            step={step}
            className="w-full text-center text-sm bg-transparent outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        ) : (
          <span className="min-w-0 max-w-full truncate text-sm">{displayValue()}</span>
        )}
      </div>

      <button
        type="button"
        aria-label={title ? `Increase ${title}` : 'Increase value'}
        className="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded hover:bg-accent disabled:opacity-50 disabled:pointer-events-none transition-colors"
        onMouseDown={startIncrement}
        onMouseUp={stopRepeat}
        onMouseLeave={stopRepeat}
        onTouchStart={startIncrement}
        onTouchEnd={stopRepeat}
        disabled={max !== undefined && value >= max}
      >
        <Icon name="plus" className="h-3 w-3" />
      </button>
    </div>
  )
}
